import type {
  CareSignalView,
  HealthStoryEntry,
  HealthSummaryMedicationItem,
  KnowledgeCoverage,
  TimelineEvent,
  TimelineViewResponse,
} from '@troyai/portal/health-timeline/data-access';
import {
  DEMO_START,
  scriptedEventUuid,
  storyDate,
  storyIso,
} from '@troyai/portal/investor-demo/core';
import { addDays, format, parseISO } from 'date-fns';

import type { Visible } from '../demo-data';
import { CLINICAL_EVENT_ID_BASE } from './ids';

/**
 * Margaret's Health story and Care signals.
 *
 * Strings follow what the backend composes in services/cmd/portal/api_v1/clinical_event_friendly_*.go:
 * "Filled <drug>" titles, "<n>-day supply · Qty <q>" summaries, "Jun 12, 2026" date labels and
 * condition names as tags. The one exception is `status_label`, which reads Filled or Paid here
 * (the backend says Active); the Timeline only draws a badge for coverage, so it is never shown.
 *
 * Dates in comments are the scripted ones, written for Tue Oct 13; every date in the data goes
 * through `storyIso` (and every date in a sentence through `storyDate`), which move it onto the
 * real story day.
 */

/** The story day: the day the page loaded, where the story starts. */
const SEED_DAY = storyIso(DEMO_START).slice(0, 10);

/** The last day with claims: everything before demo "now" has already been learned. */
const LAST_CLAIM_DAY = storyIso('2026-10-12');

/** Hidden until the "+3 days" jump. */
export const DICLOFENAC_VISIBLE_FROM = storyIso('2026-10-16');

export const DEMO_KNOWLEDGE_COVERAGE: KnowledgeCoverage = {
  enrollment: { basis: 'source_process_time', available_from: null },
  pharmacy_fill: { basis: 'projection_observed', available_from: storyIso('2026-01-01T00:00:00Z') },
  medical_claim: { basis: 'projection_observed', available_from: storyIso('2026-01-01T00:00:00Z') },
};

/** The conditions behind the tags and the Health summary. */
export const CONDITIONS = {
  chf: 'Congestive heart failure',
  diabetes: 'Type 2 diabetes mellitus',
  hypertension: 'Essential (primary) hypertension',
  ckd: 'Chronic kidney disease, stage 3a',
  osteoarthritis: 'Osteoarthritis of knee',
  lipids: 'Hyperlipidemia, unspecified',
  reflux: 'Gastro-esophageal reflux disease',
  fluid: 'Fluid overload, unspecified',
} as const;

type StoryKey =
  | 'meloxicamFirstFill'
  | 'meloxicamLastFill'
  | 'meloxicamDiscontinued'
  | 'diclofenacFill'
  | 'furosemideLateFill'
  | 'lastStatinFill'
  | 'edVisit'
  | 'orthopedicsVisit'
  | 'hraCall'
  | 'agentNote'
  | 'atorvastatinPickup'
  | 'vaccinesGiven';

export interface StorySeed {
  key?: StoryKey;
  category: HealthStoryEntry['category'];
  date: string;
  title: string;
  summary: string;
  tags: string[];
  statusLabel: string;
  recentlyUpdated?: boolean;
  visibleFrom?: string;
  /** The drug a fill belongs to, for the Health summary's medication list. */
  drug?: string;
  daysSupply?: number;
}

interface FillPlan {
  drug: string;
  name: string;
  daysSupply: number;
  quantity: number;
  tags: string[];
  dates: string[];
  keys?: Partial<Record<string, StoryKey>>;
  visibleFrom?: string;
}

/** `first`, then every `every` days, up to the last day with claims. */
function refills(first: string, every = 30): string[] {
  const dates: string[] = [];
  let day = parseISO(first);
  while (format(day, 'yyyy-MM-dd') <= LAST_CLAIM_DAY) {
    dates.push(format(day, 'yyyy-MM-dd'));
    day = addDays(day, every);
  }
  return dates;
}

const FILL_PLANS: FillPlan[] = [
  {
    drug: 'furosemide',
    name: 'Furosemide 20 mg Tablet',
    daysSupply: 30,
    quantity: 30,
    tags: [CONDITIONS.chf, CONDITIONS.ckd],
    // Due Jul 21 (30 days after Jun 21); filled two weeks late on Aug 4.
    dates: [
      storyIso('2026-01-22'),
      storyIso('2026-02-21'),
      storyIso('2026-03-23'),
      storyIso('2026-04-22'),
      storyIso('2026-05-22'),
      storyIso('2026-06-21'),
      storyIso('2026-08-04'),
      storyIso('2026-09-03'),
      storyIso('2026-10-03'),
    ],
    keys: { [storyIso('2026-08-04')]: 'furosemideLateFill' },
  },
  {
    drug: 'metoprolol',
    name: 'Metoprolol Succinate Er 50 mg Tablet',
    daysSupply: 30,
    quantity: 30,
    tags: [CONDITIONS.chf, CONDITIONS.hypertension],
    dates: refills(storyIso('2026-01-09')),
  },
  {
    drug: 'lisinopril',
    name: 'Lisinopril 10 mg Tablet',
    daysSupply: 30,
    quantity: 30,
    tags: [CONDITIONS.hypertension, CONDITIONS.ckd],
    dates: refills(storyIso('2026-01-05')),
  },
  {
    drug: 'metformin',
    name: 'Metformin Hcl 500 mg Tablet',
    daysSupply: 30,
    quantity: 60,
    tags: [CONDITIONS.diabetes],
    dates: refills(storyIso('2026-01-12')),
  },
  {
    drug: 'empagliflozin',
    name: 'Empagliflozin 10 mg Tablet',
    daysSupply: 30,
    quantity: 30,
    tags: [CONDITIONS.diabetes, CONDITIONS.chf, CONDITIONS.ckd],
    dates: refills(storyIso('2026-01-15')),
  },
  {
    drug: 'amlodipine',
    name: 'Amlodipine Besylate 5 mg Tablet',
    daysSupply: 30,
    quantity: 30,
    tags: [CONDITIONS.hypertension],
    dates: refills(storyIso('2026-01-18')),
  },
  {
    // Six fills across Jan 14 to Sep 25 cover 180 of 254 days: 71%. Out of supply since Sep 25.
    drug: 'atorvastatin',
    name: 'Atorvastatin Calcium 20 mg Tablet',
    daysSupply: 30,
    quantity: 30,
    tags: [CONDITIONS.lipids, CONDITIONS.diabetes],
    dates: [
      storyIso('2026-01-14'),
      storyIso('2026-02-18'),
      storyIso('2026-03-30'),
      storyIso('2026-05-12'),
      storyIso('2026-07-01'),
      storyIso('2026-08-26'),
    ],
    keys: { [storyIso('2026-08-26')]: 'lastStatinFill' },
  },
  {
    drug: 'omeprazole',
    name: 'Omeprazole 20 mg Capsule',
    daysSupply: 90,
    quantity: 90,
    tags: [CONDITIONS.reflux],
    dates: [storyIso('2026-01-20'), storyIso('2026-04-20'), storyIso('2026-07-19')],
  },
  {
    // Started by the orthopedist on Jun 12, refilled monthly through Sep 16. No October fill.
    drug: 'meloxicam',
    name: 'Meloxicam 15 mg Tablet',
    daysSupply: 30,
    quantity: 30,
    tags: [CONDITIONS.osteoarthritis],
    dates: [
      storyIso('2026-06-12'),
      storyIso('2026-07-14'),
      storyIso('2026-08-15'),
      storyIso('2026-09-16'),
    ],
    keys: {
      [storyIso('2026-06-12')]: 'meloxicamFirstFill',
      [storyIso('2026-09-16')]: 'meloxicamLastFill',
    },
  },
  {
    // Appears after the "+3 days" jump.
    drug: 'diclofenac',
    name: 'Diclofenac Sodium 1% Gel',
    daysSupply: 30,
    quantity: 100,
    tags: [CONDITIONS.osteoarthritis],
    dates: [DICLOFENAC_VISIBLE_FROM],
    keys: { [DICLOFENAC_VISIBLE_FROM]: 'diclofenacFill' },
    visibleFrom: DICLOFENAC_VISIBLE_FROM,
  },
];

function fillSeeds(): StorySeed[] {
  return FILL_PLANS.flatMap((plan) =>
    plan.dates.map((date): StorySeed => ({
      key: plan.keys?.[date],
      category: 'medication',
      date,
      title: `Filled ${plan.name}`,
      summary: `${plan.daysSupply}-day supply · Qty ${plan.quantity}`,
      tags: plan.tags,
      statusLabel: 'Filled',
      recentlyUpdated: plan.visibleFrom !== undefined,
      visibleFrom: plan.visibleFrom,
      drug: plan.drug,
      daysSupply: plan.daysSupply,
    }))
  );
}

function visitSeeds(): StorySeed[] {
  const visit = (
    date: string,
    title: string,
    summary: string,
    tags: string[],
    key?: StoryKey
  ): StorySeed => ({ key, category: 'visit', date, title, summary, tags, statusLabel: 'Paid' });
  return [
    visit(storyIso('2026-02-11'), 'Office visit', 'Primary care follow-up and Hemoglobin A1c', [
      CONDITIONS.diabetes,
      CONDITIONS.hypertension,
    ]),
    visit(storyIso('2026-03-18'), 'Office visit', 'Cardiology follow-up and Echocardiogram', [
      CONDITIONS.chf,
      CONDITIONS.hypertension,
    ]),
    visit(
      storyIso('2026-05-13'),
      'Office visit',
      'Primary care follow-up and Basic metabolic panel',
      [CONDITIONS.diabetes, CONDITIONS.ckd]
    ),
    visit(
      storyIso('2026-06-12'),
      'Office visit',
      'Orthopedics new patient visit and Knee X-ray',
      [CONDITIONS.osteoarthritis],
      'orthopedicsVisit'
    ),
    visit(storyIso('2026-07-20'), 'Office visit', 'Cardiology follow-up and Electrocardiogram', [
      CONDITIONS.chf,
    ]),
    visit(
      storyIso('2026-08-19'),
      'Emergency room visit',
      'Emergency department visit, high severity and Chest X-ray',
      [CONDITIONS.fluid, CONDITIONS.chf],
      'edVisit'
    ),
  ];
}

function coverageSeed(): StorySeed {
  return {
    category: 'coverage',
    date: storyIso('2024-01-01'),
    title: 'Enrolled in Troy Medicare Advantage',
    summary: 'Jan 1, 2024 – present',
    tags: [],
    statusLabel: 'Active',
  };
}

/** The care manager's calls and notes. The pharmacy story brings its own. */
function careInteractionSeeds(): StorySeed[] {
  const interaction = (
    date: string,
    title: string,
    summary: string,
    tags: string[],
    statusLabel: string,
    key?: StoryKey
  ): StorySeed => ({ key, category: 'interaction', date, title, summary, tags, statusLabel });
  return [
    interaction(
      storyIso('2026-09-24'),
      'HRA call · Troy Agent · 14 min',
      'Health risk assessment completed by phone and submitted',
      ['Health risk assessment'],
      'Completed',
      'hraCall'
    ),
    interaction(
      storyIso('2026-09-24'),
      'Agent note',
      'Lost her car in June; lives alone; daughter in Raleigh',
      ['Social needs'],
      'Logged',
      'agentNote'
    ),
    interaction(
      storyIso('2026-10-07'),
      'Outreach call · Troy Agent · 9 min',
      'Explained the plan, confirmed scale at home.',
      ['Care plan outreach'],
      'Completed'
    ),
    interaction(
      storyIso('2026-10-11'),
      'Outreach call · Troy Agent · 6 min',
      'Member confirmed daily weights started; asked whether she can "just stop the pain pill". Not answered, flagged for pharmacist.',
      ['Medication question'],
      'Completed'
    ),
  ];
}

export interface BuiltStory {
  /** Newest first, as the backend serves them. */
  entries: Visible<HealthStoryEntry>[];
  keyed: Record<StoryKey, Visible<HealthStoryEntry>>;
  /** Each drug's latest fill that has already happened (the Health summary's medication list). */
  lastFills: { drug: string; entry: HealthStoryEntry; daysSupply: number }[];
}

/**
 * The claims story with the seeds that differ between the stories: the calls and notes
 * (`interactions`) and any fills beyond the plans (`extraFills`).
 */
export function buildStory(interactions: StorySeed[], extraFills: StorySeed[] = []): BuiltStory {
  const seeds = [
    ...fillSeeds(),
    ...extraFills,
    {
      key: 'meloxicamDiscontinued' as const,
      category: 'medication' as const,
      date: storyIso('2026-10-14'),
      title: 'Discontinued Meloxicam 15 mg Tablet',
      summary: 'Stopped by Dr. Patel (orthopedics) · CancelRx sent to the pharmacy',
      tags: [CONDITIONS.osteoarthritis],
      statusLabel: 'Discontinued',
      recentlyUpdated: true,
      visibleFrom: DICLOFENAC_VISIBLE_FROM,
    },
    ...visitSeeds(),
    coverageSeed(),
    ...interactions,
  ];
  // Oldest first, so a newer event always has the larger clinical event id.
  const chronological = seeds
    .map((seed, index) => ({ seed, index }))
    .sort((a, b) =>
      a.seed.date === b.seed.date ? a.index - b.index : a.seed.date < b.seed.date ? -1 : 1
    )
    .map(({ seed }) => seed);

  const keyed: Partial<Record<StoryKey, Visible<HealthStoryEntry>>> = {};
  const lastFills = new Map<string, { entry: HealthStoryEntry; daysSupply: number }>();

  const chronologicalEntries = chronological.map((seed, offset): Visible<HealthStoryEntry> => {
    const id = CLINICAL_EVENT_ID_BASE + offset;
    const entry: Visible<HealthStoryEntry> = {
      event_id: scriptedEventUuid(id),
      clinical_event_id: id,
      category: seed.category,
      date: seed.date,
      date_label: format(parseISO(seed.date), 'MMM d, yyyy'),
      status: 'active',
      status_label: seed.statusLabel,
      recently_updated: seed.recentlyUpdated ?? false,
      tags: seed.tags,
      title: seed.title,
      summary: seed.summary,
      ...(seed.visibleFrom ? { visibleFrom: seed.visibleFrom } : {}),
    };
    if (seed.key) {
      keyed[seed.key] = entry;
    }
    if (seed.drug && seed.daysSupply && !seed.visibleFrom) {
      lastFills.set(seed.drug, { entry, daysSupply: seed.daysSupply });
    }
    return entry;
  });

  return {
    entries: chronologicalEntries.reverse(),
    keyed: keyed as Record<StoryKey, Visible<HealthStoryEntry>>,
    lastFills: [...lastFills.entries()].map(([drug, { entry, daysSupply }]) => ({
      drug,
      entry,
      daysSupply,
    })),
  };
}

const KEYED = buildStory(careInteractionSeeds()).keyed;

/** The story entries that other fixtures (evidence, episodes, care signals) point at. */
export const KEY_EVENTS = {
  /** Meloxicam started by the orthopedist, Jun 12. */
  meloxicamFirstFill: KEYED.meloxicamFirstFill,
  /** The last meloxicam fill, Sep 16: no October fill follows. */
  meloxicamLastFill: KEYED.meloxicamLastFill,
  /** Meloxicam discontinued by Dr. Patel on Oct 14, hidden until the "+3 days" jump. */
  meloxicamDiscontinued: KEYED.meloxicamDiscontinued,
  /** Diclofenac gel, three days after the story day, hidden until the "+3 days" jump. */
  diclofenacFill: KEYED.diclofenacFill,
  /** The furosemide fill two weeks late, Aug 4 (due Jul 21). */
  furosemideLateFill: KEYED.furosemideLateFill,
  /** The last atorvastatin fill, Aug 26; supply ran out Sep 25. */
  lastStatinFill: KEYED.lastStatinFill,
  /** The ED visit for fluid overload, Aug 19. */
  edVisit: KEYED.edVisit,
  /** The orthopedics visit, Jun 12. */
  orthopedicsVisit: KEYED.orthopedicsVisit,
  /** The HRA call, Sep 24. */
  hraCall: KEYED.hraCall,
  /** The agent's note about the lost car, Sep 24. */
  agentNote: KEYED.agentNote,
} as const;

/** All story entries, newest first. Fresh objects on every call, so the store may mutate them. */
export function seedStoryEntries(): Visible<HealthStoryEntry>[] {
  return buildStory(careInteractionSeeds()).entries;
}

/** The Health summary's medication list of a story: each drug's latest fill, newest first. */
export function medicationItems(story: BuiltStory): HealthSummaryMedicationItem[] {
  return story.lastFills
    .map(({ entry, daysSupply }) => ({
      event_id: entry.event_id,
      name: entry.title.replace(/^Filled /, ''),
      last_filled: entry.date,
      days_supply: daysSupply,
    }))
    .sort((a, b) => (a.last_filled < b.last_filled ? 1 : a.last_filled > b.last_filled ? -1 : 0));
}

/** The care story's Health summary medication list (9 drugs). */
export function seedMedicationItems(): HealthSummaryMedicationItem[] {
  return medicationItems(buildStory(careInteractionSeeds()));
}

// ---- Care signals ----------------------------------------------------------------------------
//
// The gaps, upcoming and transitions cards read technical timeline events with a `view_context`
// (`careSignalRows` in health-timeline-ui). The backend only produces medication and coverage
// kinds, so the statin adherence, the eye exam and the pharmacy story's vaccine and pickup cards
// use two demo kinds, `care_gap` and `care_due`, whose own words travel in `details.detail` and
// `details.note`.

/** The eye exam has no claim behind it, so its care signals point at no story entry. */
export const EYE_EXAM_SIGNAL_EVENT_ID = scriptedEventUuid(CLINICAL_EVENT_ID_BASE + 99);

function viewResponse(view: CareSignalView, events: TimelineEvent[]): TimelineViewResponse {
  return {
    events,
    view,
    as_of_valid: SEED_DAY,
    as_of_knowledge: storyIso(DEMO_START),
    knowledge_coverage: DEMO_KNOWLEDGE_COVERAGE,
  };
}

/**
 * The cards both stories show, the statin adherence gap and the ED visit, pointing at the given
 * story's own entries (the ids differ between the stories). `extra` adds the cards that differ.
 */
export function buildCareSignals(
  keyed: Pick<BuiltStory['keyed'], 'lastStatinFill' | 'edVisit'>,
  extra: { gaps: TimelineEvent[]; upcoming: TimelineEvent[] }
): Record<CareSignalView, TimelineViewResponse> {
  const statinFill = keyed.lastStatinFill;
  const ed = keyed.edVisit;

  const statinAdherence: TimelineEvent = {
    event_id: statinFill.event_id,
    version: 1,
    lane: 'pharmacy_fill',
    event_type: 'pharmacy_fill',
    status: 'active',
    valid_from: statinFill.date,
    valid_to: storyIso('2026-09-25'),
    details: {
      dispensable_name: 'Atorvastatin Calcium 20 mg Tablet',
      days_supply: 30,
      detail: 'Adherence 71% · below the 80% goal',
      note: `Out of supply since ${storyDate('2026-09-25', 'MMM d')} · Last fill ${storyDate('2026-08-26', 'MMM d')} · Atorvastatin Calcium 20 mg Tablet`,
    },
    view_context: {
      kind: 'care_gap',
      state: 'open',
      measure: 'STATINS',
      gap_start: storyIso('2026-09-25'),
      gap_end: '',
      duration_days: 18,
    },
  };

  const edTransition: TimelineEvent = {
    event_id: ed.event_id,
    version: 1,
    lane: 'medical_claim',
    event_type: 'medical_claim',
    status: 'active',
    valid_from: ed.date,
    valid_to: storyIso('2026-08-20'),
    details: { practice_name: 'QUEEN CITY GENERAL HOSPITAL', place_of_service: '23' },
    view_context: {
      kind: 'acute_transition',
      transition_type: 'emergency_department',
      transition_date: ed.date,
      days_since: 55,
    },
  };

  return {
    gaps: viewResponse('gaps', [statinAdherence, ...extra.gaps]),
    upcoming: viewResponse('upcoming', extra.upcoming),
    transitions: viewResponse('transitions', [edTransition]),
  };
}

export function seedCareSignals(): Record<CareSignalView, TimelineViewResponse> {
  const eyeExamGap: TimelineEvent = {
    event_id: EYE_EXAM_SIGNAL_EVENT_ID,
    version: 1,
    lane: 'medical_claim',
    event_type: 'care_gap',
    status: 'active',
    valid_from: storyIso('2026-01-01'),
    valid_to: null,
    details: {
      detail: 'Not done this year',
      note: 'No eye exam claim since Jan 1, 2026',
    },
    view_context: {
      kind: 'care_gap',
      state: 'open',
      measure: 'Diabetic eye exam',
      gap_start: storyIso('2026-01-01'),
      gap_end: '',
      duration_days: 285,
    },
  };

  const eyeExamDue: TimelineEvent = {
    event_id: EYE_EXAM_SIGNAL_EVENT_ID,
    version: 1,
    lane: 'medical_claim',
    event_type: 'care_due',
    status: 'active',
    valid_from: storyIso('2026-01-01'),
    valid_to: null,
    details: {
      detail: `Due ${storyDate('2026-12-15', 'MMM d')} · in 63 days`,
      note: 'Part of the diabetic eye exam episode',
    },
    view_context: {
      kind: 'care_due',
      measure: 'Diabetic eye exam',
      due_date: storyIso('2026-12-15'),
      days_until_due: 63,
    },
  };

  return buildCareSignals(KEY_EVENTS, { gaps: [eyeExamGap], upcoming: [eyeExamDue] });
}
